import {
  Badge,
  Button,
  Checkbox,
  DateRangePicker,
  Dialog,
  Icon,
  IconSizeClasses,
  Label,
  QuickSelectOption,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import { format, startOfDay, subDays } from 'date-fns';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useGetDeviceTypeConfigs } from '$components/app-config/app-config.api';
import { DeviceNameCell } from '$components/device/name-cell';
import { useGetDevice } from '$features/devices/devices.api';
import { useDeviceSnapshot } from '$features/monitoring/device-snapshot.api';
import { getIconNameForParameterName } from '$features/monitoring/monitoring.utils';
import { useDeviceTelemetryDataForSnapshot } from '$features/monitoring/parameters/useDeviceTelemetryDataForSnapshot';
import {
  getMaxAllowedDays,
  useGetSubscription,
} from '$features/subscription/subscription-api';

import './monitoring-report-dialogs.scss';

export interface MonitoringReportDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  manufacturer: string;
  initialDateRange?: { from?: Date; to?: Date };
  closeOnOutsideClick?: boolean;
  onSave: (data: {
    deviceId: string;
    dateRange: { from: Date; to: Date };
    notes?: string;
    deviceName?: string;
    deviceModel?: string;
    selectedTelemetryIds: string[];
  }) => Promise<void>;
}

export function MonitoringReportDialogs({
  isOpen,
  onOpenChange,
  deviceId,
  manufacturer,
  initialDateRange,
  closeOnOutsideClick = false,
  onSave,
}: MonitoringReportDialogProps): ReactElement {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>(
    initialDateRange ?? {},
  );
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [selectedTelemetryIds, setSelectedTelemetryIds] = useState<string[]>([]);
  const [selectedQuickOption, setSelectedQuickOption] = useState<
    QuickSelectOption | undefined
  >(undefined);
  const initializedDevice = useRef<{ deviceId: string; manufacturer: string } | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen) {
      initializedDevice.current = null;
      return;
    }
    if (
      initializedDevice.current?.deviceId === deviceId &&
      initializedDevice.current.manufacturer === manufacturer
    )
      return;

    initializedDevice.current = { deviceId, manufacturer };
    const now = new Date();
    setDateRange({
      ...initialDateRange,
      to: initialDateRange?.to && initialDateRange.to > now ? now : initialDateRange?.to,
    });
    setNotes('');
    setSaveError(false);
    setSelectedTelemetryIds([]);
    setSelectedQuickOption(undefined);
  }, [isOpen, initialDateRange, deviceId, manufacturer]);

  const { data: device } = useGetDevice({
    serialNumber: deviceId,
    manufacturer,
    enabled: isOpen,
  });

  const { data: subscription } = useGetSubscription();
  const {
    isLoading: isLoadingDeviceConfigs,
    isError: deviceConfigsError,
    refetch: refetchDeviceConfigs,
  } = useGetDeviceTypeConfigs();
  const {
    data: snapshot,
    isLoading: isLoadingSnapshot,
    isPlaceholderData,
    isError: snapshotError,
    refetch: refetchSnapshot,
  } = useDeviceSnapshot({ manufacturer, serialNumber: deviceId }, isOpen);
  const { primaryParameters } = useDeviceTelemetryDataForSnapshot(snapshot);
  const selectedParameters = primaryParameters.filter((parameter) =>
    selectedTelemetryIds.includes(parameter.telemetryId),
  );
  const isLoadingParameters =
    isLoadingSnapshot || isPlaceholderData || isLoadingDeviceConfigs;
  const parametersError = snapshotError || deviceConfigsError;
  const parametersUnavailable =
    isLoadingParameters || parametersError || primaryParameters.length === 0;
  const maxAllowedDays = getMaxAllowedDays(subscription);
  const disabledDates = {
    before: subDays(new Date(), maxAllowedDays),
    after: new Date(),
  };

  const maxNotesLength = 2000;
  const rangeInvalid = Boolean(
    dateRange.from &&
    dateRange.to &&
    (!Number.isFinite(dateRange.from.getTime()) ||
      !Number.isFinite(dateRange.to.getTime()) ||
      dateRange.to <= dateRange.from ||
      dateRange.to > disabledDates.after ||
      dateRange.from < startOfDay(disabledDates.before) ||
      dateRange.to.getTime() - dateRange.from.getTime() > 90 * 24 * 60 * 60 * 1000),
  );
  const isSaveDisabled =
    !dateRange.from ||
    !dateRange.to ||
    rangeInvalid ||
    isSubmitting ||
    parametersUnavailable ||
    selectedParameters.length === 0;

  const handleSave = async () => {
    if (isSaveDisabled || !dateRange.from || !dateRange.to) {
      return;
    }

    setIsSubmitting(true);
    setSaveError(false);
    try {
      await onSave({
        deviceId,
        dateRange: { from: dateRange.from, to: dateRange.to },
        notes: notes || undefined,
        deviceName: device?.name,
        deviceModel: device?.model,
        selectedTelemetryIds: selectedParameters.map(
          (parameter) => parameter.telemetryId,
        ),
      });
    } catch {
      setSaveError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
    setDateRange(range || {});
    setSelectedQuickOption(undefined);
  };

  const handleOpenChange = (open: boolean) => {
    if (isSubmitting || (!open && !closeOnOutsideClick)) {
      return;
    }
    onOpenChange(open);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      className="monitoring-report-dialog w-max-4xl"
      hideCloseButton
    >
      <Button
        type="button"
        variant="icon"
        className="dialog-content__close-x"
        title="close"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        <i className="icon ic_close icon--m" title="close" />
      </Button>

      <Dialog.Title className="monitoring-report-dialog__title m-bottom-l">
        <span className="flex flex__ai--center gap-s">
          {t('monitoring.report.dialog.title')}
          <Tooltip
            trigger={
              <div style={{ display: 'inline-block' }}>
                <Badge
                  color="purple"
                  size="small"
                  className="flex flex__ai--center gap-xs bg-purple-500 color-white font-weight-bold"
                >
                  <Icon name="flag" size={IconSizeClasses.XXSmall} className="bg-white" />
                  {t('reportHub.beta.badge')}
                </Badge>
              </div>
            }
          >
            {t(
              'reportHub.beta.tooltip',
              'Early access: this feature is in beta until the beginning of 2027 and may change or be removed. Your feedback helps shape VisioNize Lab Suite.',
            )}
          </Tooltip>
        </span>
      </Dialog.Title>

      {device && (
        <div className="m-bottom-l">
          <DeviceNameCell device={device} />
        </div>
      )}

      <div className="flex flex__dir--column gap-l">
        <div className="monitoring-report-dialog__field">
          <Label htmlFor="timespan-picker">
            {t('monitoring.report.dialog.timespan')}*
          </Label>
          <DateRangePicker
            id="timespan-picker"
            dateRange={{ from: dateRange.from, to: dateRange.to }}
            onValueChange={handleDateRangeChange}
            dateFormatter={(date) => format(date, 'dd MMM yyyy, HH:mm')}
            timeSelection={{
              maximumDate: disabledDates.after,
              startLabel: t('monitoring.report.dialog.startTime'),
              endLabel: t('monitoring.report.dialog.endTime'),
            }}
            invalid={rangeInvalid}
            isDisabled={isSubmitting}
            quickSelect={{
              enabled: true,
              onOptionSelect: (option) => setSelectedQuickOption(option),
              selectedOption: selectedQuickOption,
            }}
            centered
            disabled={disabledDates}
            startMonth={disabledDates.before}
            endMonth={disabledDates.after}
            showFooterDivider={false}
          />
          {rangeInvalid && (
            <p role="alert" className="color-red-500 font-size-s">
              {t('monitoring.report.dialog.invalidPeriod')}
            </p>
          )}
        </div>

        <fieldset
          className="monitoring-report-dialog__parameters"
          disabled={isSubmitting}
        >
          <legend className="w-full">
            <span className="flex flex__jc--space-between flex__ai--center m-bottom-s">
              <span>{t('monitoring.report.dialog.parameters')}*</span>
              <Button
                variant="tertiary"
                size="small"
                disabled={isSubmitting || parametersUnavailable}
                onClick={() =>
                  setSelectedTelemetryIds(
                    selectedParameters.length
                      ? []
                      : primaryParameters.map((parameter) => parameter.telemetryId),
                  )
                }
              >
                {t(
                  selectedParameters.length
                    ? 'monitoring.report.dialog.clearAll'
                    : 'monitoring.report.dialog.selectAll',
                )}
              </Button>
            </span>
          </legend>
          {isLoadingParameters && (
            <p role="status">{t('monitoring.report.dialog.loadingParameters')}</p>
          )}
          {!isLoadingParameters && parametersError && (
            <div role="alert">
              <p>{t('monitoring.report.dialog.parametersLoadError')}</p>
              <Button
                type="button"
                variant="tertiary"
                size="small"
                disabled={isSubmitting}
                onClick={() => Promise.all([refetchSnapshot(), refetchDeviceConfigs()])}
              >
                {t('shared.tryAgain')}
              </Button>
            </div>
          )}
          {!isLoadingParameters && !parametersError && primaryParameters.length === 0 && (
            <p role="status">{t('monitoring.report.dialog.noParameters')}</p>
          )}
          {!parametersUnavailable && (
            <div className="monitoring-report-dialog__parameter-grid">
              {primaryParameters.map((parameter) => (
                <label
                  key={parameter.telemetryId}
                  className="monitoring-report-dialog__parameter"
                >
                  <Checkbox
                    ariaLabel={parameter.name ?? parameter.telemetryId}
                    checked={selectedTelemetryIds.includes(parameter.telemetryId)}
                    disabled={isSubmitting}
                    onChange={(checked) =>
                      setSelectedTelemetryIds((selected) =>
                        checked === true
                          ? [
                              ...selected.filter((id) => id !== parameter.telemetryId),
                              parameter.telemetryId,
                            ]
                          : selected.filter((id) => id !== parameter.telemetryId),
                      )
                    }
                  />
                  <Icon
                    name={getIconNameForParameterName(parameter.name)}
                    size={IconSizeClasses.XSmall}
                    className="ic_parameters"
                  />
                  <span>{parameter.name ?? parameter.telemetryId}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <div>
          <Label htmlFor="notes-textarea">{t('monitoring.report.dialog.notes')}</Label>
          <div
            className={cn(
              'p-x-s',
              'p-y-s',
              'w-full',
              'border-radius-s',
              'border-s',
              'border-solid',
              'border-color-gray-500',
            )}
          >
            <textarea
              id="notes-textarea"
              className="w-full monitoring-report-dialog__textarea"
              rows={4}
              maxLength={maxNotesLength}
              value={notes}
              disabled={isSubmitting}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <p className="m-y-xs color-gray-700 font-size-s">
            {notes.length}/{maxNotesLength}
          </p>
        </div>

        <div
          role="alert"
          className="bg-blue-50 border-solid border-color-blue-500 border-radius-xs border-s p-m"
          style={{ borderWidth: 2 }}
        >
          <div className="flex flex__ai--start gap-s">
            <Icon
              name="notice"
              size={IconSizeClasses.XSmall}
              className="color-white bg-blue-500"
            />
            <p>
              <Trans
                i18nKey="monitoring.report.dialog.infoMessage"
                components={{ bold: <span className="font-weight-bold" /> }}
              />
            </p>
          </div>
        </div>
      </div>

      {saveError && (
        <p role="alert" className="m-top-m color-red-500">
          {t('monitoring.report.dialog.saveError')}
        </p>
      )}

      <Dialog.Actions className="m-top-xl">
        <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
          {t('shared.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaveDisabled}>
          {t('shared.create')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

export interface MonitoringReportSuccessDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onViewDetails: () => void;
}

export function MonitoringReportSuccessDialog({
  isOpen,
  onOpenChange,
  onViewDetails,
}: MonitoringReportSuccessDialogProps): ReactElement {
  const { t } = useTranslation();

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleViewDetails = () => {
    onViewDetails();
    onOpenChange(false);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="monitoring-report-success-dialog w-max-4xl"
      hideCloseButton
    >
      <div className="flex flex__dir--column flex__ai--center text-align-center">
        <div className="monitoring-report-success-dialog__icon-wrapper m-bottom-l">
          <Icon name="info" className="bg-blue-500" size={IconSizeClasses.XLarge} />
        </div>

        <Dialog.Title className="m-bottom-m">
          {t('monitoring.report.success.title', 'Activity report added')}
        </Dialog.Title>

        <Dialog.Description className="color-gray-900 font-size-m m-bottom-xxs text-align-left">
          {t('monitoring.report.success.description')}
        </Dialog.Description>
      </div>

      <Dialog.Actions className="m-top-xl">
        <Button variant="secondary" onClick={handleClose}>
          {t('shared.close')}
        </Button>
        <Button variant="primary" onClick={handleViewDetails}>
          {t('monitoring.report.success.goToReportHub')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
